import { extend, isDefined } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { locate, locateNode } from "@dhx/ts-common/html";
import { Id } from "@dhx/ts-common/types";
import { TreeCollection } from "@dhx/ts-data";
import { Position, tooltip } from "@dhx/ts-message";
import {
	createFactory,
	IState,
	Navbar,
	INavbar,
	IButton,
	IImageButton,
	ISelectButton,
	IInput,
	ISeparator,
	ISpacer,
	ITitle,
	ICustomHTML,
	INavItem,
	IMenuItem,
	IDatePicker,
} from "@dhx/ts-navbar";

export interface IToolbarConfig {
	css?: string;
	menuCss?: string;
	data?: any[] | TreeCollection<IToolbarElement>;
}

export interface IToolbar extends INavbar {
	data: TreeCollection<IToolbarElement>;
	config: IToolbarConfig;

	getState(id?: Id): IState;
	setState(state: IState): void;
}

export type IToolbarElement =
	| IButton
	| IImageButton
	| ISelectButton
	| IInput
	| ISeparator
	| ISpacer
	| ITitle
	| ICustomHTML
	| INavItem
	| IMenuItem
	| IDatePicker;

export class Toolbar extends Navbar<IToolbarElement> implements IToolbar {
	data: TreeCollection<IToolbarElement>;
	config: IToolbarConfig;

	constructor(element?: string | HTMLElement, config?) {
		super(
			element,
			extend(
				{
					navigationType: "click",
				},
				config
			)
		);

		this._currentRoot = null;
		const render = () => this._draw(element);
		this.mount(element, create({ render }));
	}
	getState(id?: Id): IState {
		if (isDefined(id) && !this.data.getItem(id)) {
			return;
		}
		const state = {};
		this.data.eachChild(
			this.data.getRoot(),
			item => {
				if ((item as any).twoState && !item.group) {
					state[item.id] = (item as any).active;
				} else if (
					item.type === "input" ||
					item.type === "selectButton" ||
					item.type === "datePicker"
				) {
					state[item.id] = item.value;
				}
			},
			false
		);
		for (const key in this._groups) {
			if (this._groups[key].active) {
				state[key] = this._groups[key].active;
			}
		}
		if (id) {
			return state[id];
		}
		return state;
	}
	setState(state: IState): void {
		for (const key in state) {
			if (this._groups && this._groups[key]) {
				if (this._groups[key].active) {
					this.data.update(this._groups[key].active, { active: false });
					this._groups[key].active = state[key];
					this.data.update(state[key], { active: true });
				}
			} else {
				const item = this.data.getItem(key);
				switch (item.type) {
					case "input":
						this.data.update(key, { value: state[key] });
						break;
					case "selectButton":
						{
							const selected = item.items?.find(option => option.value === state[key]);
							this.data.update(key, { value: state[key], icon: selected?.icon });
						}
						break;
					default:
						this.data.update(key, { active: state[key] });
				}
			}
		}
	}
	protected _customHandlers() {
		return {
			input: (e: Event) => {
				const id = locate(e);
				this.data.update(id, { value: (e.target as HTMLInputElement).value });
			},
			tooltip: (e: MouseEvent) => {
				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("data-dhx-id");
				const item = this.data.getItem(id);
				if (item?.tooltip) {
					tooltip(item.tooltip, {
						node: elem as HTMLElement,
						position: Position.bottom,
					});
				}
			},
		};
	}
	protected _getFactory() {
		return createFactory({
			widget: this,
			defaultType: "navItem",
			allowedTypes: [
				"button",
				"imageButton",
				"selectButton",
				"navItem",
				"menuItem",
				"separator",
				"spacer",
				"title",
				"input",
				"customHTML",
				"datePicker",

				"customHTMLButton",
			],
			widgetName: "toolbar",
		});
	}
	protected _draw(element) {
		const getAriaAttrs = element => ({
			role: "toolbar",
			"aria-label": element || "", // to differ toolbars if there are 2 or more on page
		});

		const toolbarHeight = this.data.getLength()
			? this.data.reduce((result, item) => {
					switch (item.type) {
						case "title":
							return !result ? 20 : result;
						case "button":
							return item.size === "small" && (!result || result <= 28) ? 28 : result || 32;
						default:
							return 32;
					}
			  }, 0) + 24
			: null;

		return el(
			"nav.dhx_widget.dhx_toolbar",
			{
				style: { height: toolbarHeight },
				class: this.config.css ? this.config.css : "",
			},
			[
				el(
					"ul.dhx_navbar.dhx_navbar--horizontal",
					{
						"data-dhx-widget-id": this._uid,
						tabindex: 0,
						...getAriaAttrs(element),
						onclick: this._handlers.onclick,
						onmousedown: this._handlers.onmousedown,
						oninput: this._handlers.input,
						onmouseover: this._handlers.tooltip,
						_hooks: {
							didInsert: node => {
								node.el.addEventListener(
									"keyup",
									e => {
										if (e.which !== 9) {
											return;
										}
										const elem = locateNode(document.activeElement);
										if (!elem) {
											return;
										}
										const id = elem.getAttribute("data-dhx-id");
										const item = this.data.getItem(id);
										if (item?.tooltip) {
											tooltip(item.tooltip, {
												node: elem as HTMLElement,
												position: Position.bottom,
												force: true,
											});
										}
									},
									true
								);
							},
						},
					},
					this.data.map(item => this._factory(item), this.data.getRoot(), false)
				),
			]
		);
	}
	protected _getMode(item, root): "right" | "bottom" {
		return item.id === root ? "bottom" : "right";
	}
	protected _close(e: MouseEvent): void {
		this._activePosition = null;
		this._currentRoot = null;
		super._close(e);
	}
	protected _setRoot(id: Id) {
		if (this.data.getParent(id) === this.data.getRoot()) {
			this._currentRoot = id;
		}
	}
}
